import { ForgeDatePicker, ForgeRadio, ForgeTextField } from '@tylertech/forge-react';
import { DatesInfo, FrequencyConfig, TimezoneTypes } from 'common/types/frequencySelector/frequencySelector';
import { I18n } from 'common/i18n';
import React, { Dispatch, FC, SetStateAction, useMemo, useState } from 'react';
import { mappedTimezone } from '../helpers/timezoneHelper';
import moment from 'moment-timezone';
import { defaultDateFormat } from '../constants/dateFormats';
import FeatureFlags from 'common/feature_flags';
import { handleTimeAndDatePickerChange, isEndDateInvalid } from '../helpers/dateHelper';

const scope = 'shared.components.frequency_selector.date';
const scheduledSendModalScope = 'shared.components.schedule_send_report.modal';

const enableScheduleBackDating = FeatureFlags.valueOrDefault(
  'enable_report_scheduled_delivery_backdating',
  false,
);

export interface DatesProps {
  value: DatesInfo;
  timeZone: TimezoneTypes;
  setValue: Dispatch<SetStateAction<FrequencyConfig>>;
  isFrequencyStartDateInvalid: boolean;
  isFrequencyEndDateInvalid: boolean;
}

const Dates: FC<DatesProps> = (props) => {
  const { value, setValue, timeZone, isFrequencyStartDateInvalid, isFrequencyEndDateInvalid } = props;
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const onValueChange = <K extends keyof DatesInfo>(field: K, val: DatesInfo[K]) => {
    let updatedFormValue: Partial<DatesInfo> = {
      [field]: val,
    };

    //Reset fields on specific conditions
    if (field === 'hasEndDate')
      updatedFormValue = {
        ...updatedFormValue,
        ...(val === false && value.endDate && { endDate: undefined }),
      };

    if (field === 'startDate' && val && typeof val === 'string') {
      updatedFormValue = {
        ...updatedFormValue,
        ...(value.endDate && isEndDateInvalid(val, value.endDate) && { endDate: undefined }),
      };
    }

    setValue((prevState) => ({
      ...prevState,
      datesInfo: {
        ...prevState.datesInfo,
        ...updatedFormValue,
      },
    }));
  };

  const minStartDate = useMemo(() => {
    return moment().tz(mappedTimezone(timeZone)).format(defaultDateFormat);
  }, [timeZone]);

  const minEndDate = useMemo(() => {
    if (!value.startDate) return null;

    return moment(new Date(value.startDate)).format(defaultDateFormat);
  }, [value.startDate]);

  const startDateInvalid = !value.startDate && isFrequencyStartDateInvalid;
  const endDateInvalid = value.hasEndDate && !value.endDate && isFrequencyEndDateInvalid;

  /*
    We will remove the 'value' property from the ForgeDatePicker if we are currently updating.
    As per checking, the date picker has some autocomplete behavior if the 'value' property gets updated
   */
  const datePickerValueProp = !isUpdating
    ? {
        value: value.startDate,
      }
    : {};

  const handleForgeDatePickerChange = (event: CustomEvent) => {
    handleTimeAndDatePickerChange(isUpdating, setIsUpdating, () => onValueChange('startDate', event.detail));
  };

  return (
    <>
      <div className="picker-sub-container start-date" data-testid="frequency-selector-start-date">
        <p className="forge-typography--body2">{I18n.t('start_date_label', { scope })}</p>
        <ForgeDatePicker
          data-testid="start-date-picker"
          className="date-picker"
          popup-classes="datatype-popup"
          valueMode="string"
          allowInvalidDate={enableScheduleBackDating}
          min={enableScheduleBackDating ? null : minStartDate}
          on-forge-date-picker-change={handleForgeDatePickerChange}
          {...datePickerValueProp}
        >
          <ForgeTextField required={startDateInvalid} invalid={startDateInvalid}>
            <input
              type="text"
              id="input-datepicker"
              data-testid="start-date-picker-input-field"
              placeholder={value.startDate || ''}
              className="scheduled-send-start-date"
              onBlur={() => setIsUpdating(false)}
            />
            {!value.startDate && (
              <label htmlFor="input-datepicker">{I18n.t('date_field_placeholder', { scope })}</label>
            )}
            {startDateInvalid && (
              <span slot="support-text">{I18n.t('required_field', { scope: scheduledSendModalScope })}</span>
            )}
          </ForgeTextField>
        </ForgeDatePicker>
      </div>
      <div className="picker-sub-container end-date" data-testid="frequency-selector-end-date">
        <p className="forge-typography--body2">{I18n.t('end_date_label', { scope })}</p>
        <div role="radiogroup" className="radio-group-end-date">
          <ForgeRadio
            data-testid="has-no-end-date-option"
            checked={!value.hasEndDate}
            disabled={!value.startDate}
            onClick={() => onValueChange('hasEndDate', false)}
          >
            {I18n.t('no_end_date_label', { scope })}
          </ForgeRadio>
          <ForgeRadio
            data-testid="has-end-date-option"
            checked={value.hasEndDate}
            disabled={!value.startDate}
            onClick={() => onValueChange('hasEndDate', true)}
          ></ForgeRadio>
          <ForgeDatePicker
            className="date-picker"
            data-testid="end-date-picker"
            popup-classes="datatype-popup"
            valueMode="string"
            allowInvalidDate={false}
            value={value.endDate}
            disabled={!value.hasEndDate || !value.startDate}
            min={minEndDate}
            on-forge-date-picker-change={(event: CustomEvent) => onValueChange('endDate', event.detail)}
          >
            <ForgeTextField required={endDateInvalid} invalid={endDateInvalid}>
              <input type="text" id="input-datepicker" data-testid="end-date-picker-input-field" />
              {!value.endDate && (
                <label htmlFor="input-datepicker">{I18n.t('date_field_placeholder', { scope })}</label>
              )}
              {endDateInvalid && (
                <span slot="support-text">
                  {I18n.t('required_field', { scope: scheduledSendModalScope })}
                </span>
              )}
            </ForgeTextField>
          </ForgeDatePicker>
        </div>
      </div>
    </>
  );
};

export default Dates;
